import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { useSelfService } from "@/hooks/useSelfService";
import { CircleCheck } from "lucide-react";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import logo from "/asset/logo-removebg-preview.png";
import { FilePond, registerPlugin } from 'react-filepond'
import 'filepond/dist/filepond.min.css'
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation'
import FilePondPluginImagePreview from 'filepond-plugin-image-preview'
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css'
import { FilePondFile } from 'filepond';
import '@/App.css'
import { navigate } from "wouter/use-browser-location";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/utils/axiosInterceptor";

registerPlugin(FilePondPluginImageExifOrientation, FilePondPluginImagePreview)

type ReceiptDataType = {
  companyRegistrationNumber?: string;
  companyName: string;
  invoiceNumber: string;
  invoiceDateTime?: string;
  originalInvoiceNumber: string;
  invoiceItems: Array<{
    itemDescription?: string;
    itemPrice: number;
    itemQuantity: number;
    itemSubtotal: number;
    itemServiceChargeRate: number;
    itemSalesTaxRate: number;
    itemDiscountRate: number;
  }>;
  totalDiscountAmount: number;
  totalSalesTaxAmount: number;
  totalServiceChargeAmount: number;
  totalPayableAmount: number;
  totalRoundingAmount: number;
};

const SelfServiceOcrPage = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [files, setFiles] = useState<FilePondFile[]>([])
  const [isChecked, setIsChecked] = useState(false);
  const [isFileError, setIsFileError] = useState<null | string>(null)
  const [isTermsError, setIsTermsError] = useState<null | string>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const queryClient = useQueryClient();

  const {
    performOcr
  } = useSelfService()

  useEffect(() => {
    if (files) {
      setIsFileError(null)
    }
    
    if (isChecked) {
      setIsTermsError(null)
    }
  }, [files, isChecked])


  const handleOcrAnalyse = async () => {
    if (!isChecked || files.length == 0) {
      if (!isChecked) {
        setIsTermsError("Please agree to the terms and condition")
      }
      
      if (files.length == 0) {
        setIsFileError("Please upload a receipt image")
      }

      return
    }

    // OCR Login goes here
    setIsLoading(true)
    try {
      const receiptData: ReceiptDataType = await performOcr(files[0].file as File)

      if (!receiptData) {
        throw new Error("Unable to get response, please try again")
      }

      const companyProfile = await queryClient.fetchQuery({
        queryKey: ["selfServiceInvoice", receiptData.companyRegistrationNumber],
        queryFn: async () => {
          try {
            const response = await api.get(
              `/self-service/check-precondition-before-ocr?`,
              {
                params: {
                  searchQuery:
                    receiptData.companyRegistrationNumber != null
                      ? receiptData.companyRegistrationNumber
                      : receiptData.companyName,
                  originalInvoiceNumber: receiptData.originalInvoiceNumber,
                },
              }
            );

            return response.data;
          } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message || "Unexpected error");
          }
        },
      });

      toast({
        title: "OCR Completion",
        description: 'You OCR is completed',
        duration: 3000,
        icon: <CircleCheck size={40} className="text-green-300 animate-spin-once" />
      })
      setTimeout(() => {navigate("self-service-ocr-document", { state: { receiptData, companyProfile } })}, 3000)
    } catch (error: any) {
      console.error(`Failed to perform OCR: ${error.message}`)
 
      toast({
        title: "Oops! Something went wrong",
        variant: "destructive",
        description: error.message,
        duration: 8000,
      })

      setIsLoading(false)
    }
  };

  const handleFileUpdate = (updatedFiles: any) => {
    setFiles(updatedFiles);

    if (files) {
      setIsFileError(null); // Clear the error when a file is present
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center py-8 relative overflow-hidden bg-gradient-to-br from-blue-600 to-purple-700">
      <div className="absolute inset-0" style={{ opacity: 0.8 }}>
        <svg
          className="w-full h-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          role="img"
          aria-labelledby="waveTitle"
        >
          <title id="waveTitle">Animated wave background</title>
          {/* Wave Group 1 */}
          <path
            fill="rgba(59, 130, 246, 0.2)"
            d="M-150 40 C-100 50, -50 30, 0 40 S 50 50, 100 40 S 150 30, 200 40 V100 H-150 Z"
            className="animate-wave-slow"
          >
            <animate
              attributeName="d"
              dur="10s"
              repeatCount="indefinite"
              values="
                M-150 40 C-100 50, -50 30, 0 40 S 50 50, 100 40 S 150 30, 200 40 V100 H-150 Z;
                M-150 30 C-100 40, -50 50, 0 30 S 50 40, 100 50 S 150 40, 200 30 V100 H-150 Z;
                M-150 40 C-100 50, -50 30, 0 40 S 50 50, 100 40 S 150 30, 200 40 V100 H-150 Z"
            />
          </path>

          {/* Wave Group 2 */}
          <path
            fill="rgba(139, 92, 246, 0.2)"
            d="M-150 50 C-100 60, -50 40, 0 50 S 50 60, 100 50 S 150 40, 200 50 V100 H-150 Z"
            className="animate-wave-medium"
          >
            <animate
              attributeName="d"
              dur="8s"
              repeatCount="indefinite"
              values="
                M-150 50 C-100 60, -50 40, 0 50 S 50 60, 100 50 S 150 40, 200 50 V100 H-150 Z;
                M-150 40 C-100 50, -50 60, 0 40 S 50 50, 100 60 S 150 50, 200 40 V100 H-150 Z;
                M-150 50 C-100 60, -50 40, 0 50 S 50 60, 100 50 S 150 40, 200 50 V100 H-150 Z"
            />
          </path>

          {/* Wave Group 3 */}
          <path
            fill="rgba(99, 102, 241, 0.2)"
            d="M-150 60 C-100 70, -50 50, 0 60 S 50 70, 100 60 S 150 50, 200 60 V100 H-150 Z"
            className="animate-wave-fast"
          >
            <animate
              attributeName="d"
              dur="6s"
              repeatCount="indefinite"
              values="
                M-150 60 C-100 70, -50 50, 0 60 S 50 70, 100 60 S 150 50, 200 60 V100 H-150 Z;
                M-150 50 C-100 60, -50 70, 0 50 S 50 60, 100 70 S 150 60, 200 50 V100 H-150 Z;
                M-150 60 C-100 70, -50 50, 0 60 S 50 70, 100 60 S 150 50, 200 60 V100 H-150 Z"
            />
          </path>
        </svg>

        {/* Floating Particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <div
              key={`particle-${i}-${Math.random().toString(36)}`}
              className="absolute rounded-full bg-white/10 animate-float"
              style={{
                width: `${Math.random() * 10 + 5}px`,
                height: `${Math.random() * 10 + 5}px`,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${Math.random() * 10 + 10}s`,
              }}
            />
          ))}
        </div>
      </div>
      <Card className="w-[95%] lg:w-1/2 bg-white/90 backdrop-blur-sm shadow-xl relative z-10 p-1">
        <CardHeader className="gap-2">
          <CardTitle className="flex items-center justify-center">
            <img className="h-16" 
              // src="/logo-removebg-preview.png"
              src={logo}
            />
          </CardTitle>
          <CardDescription className="text-black">
            Upload a receipt to OCR the receipt details
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="">
            { isFileError && (
              <p className="text-sm text-red-600">{isFileError}</p>
            )}
            <FilePond
              onupdatefiles={handleFileUpdate}
              allowImagePreview={true}
              imagePreviewMaxFileSize="5MB"
              acceptedFileTypes={['image/*']}
              allowMultiple={false}
              maxFiles={1}
              name="files" /* sets the file input name, it's filepond by default */
              labelIdle='Drag & Drop your files or <span class="filepond--label-action">Browse</span>'
              instantUpload={false}
              dropOnPage={true}
              dropOnElement={false}
              disabled={isLoading}
            />
          </div>
            <div className="flex items-center gap-2.5 text-sm">
              <Input className="size-4" type="checkbox" checked={isChecked} onChange={(e) => {setIsChecked(e.target.checked);setIsTermsError(null)}} disabled={isLoading}/>
              <p>
                I hereby agree with the{" "}
                <a
                  className="hover:underline text-blue-500 cursor-pointer"
                  onClick={() => setIsOpen(true)}
                >
                  terms and conditions
                </a>
                .
              </p>
              <TermAndCondition isOpen={isOpen} setIsOpen={setIsOpen} />
              </div>
              { isTermsError && (
                <p className="text-sm text-red-600 mt-1">{isTermsError}</p>
              )}
            <div className="flex justify-center w-full mt-4">
              <Button 
                className="md:w-1/5 w-full"
                onClick={handleOcrAnalyse}
                disabled={isLoading}
              >
                {isLoading ? <p className="animate-pulse">Analyzing ......</p> : `Analyse`}
              </Button>
            </div>
        </CardContent>
      </Card>
    </div>
  );
};

type TermsAndConditionProp = {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
};
const TermAndCondition = ({ isOpen, setIsOpen }: TermsAndConditionProp) => {
  return (
    <Dialog open={isOpen} onOpenChange={() => setIsOpen(false)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Terms and Conditions</DialogTitle>
        </DialogHeader>
        <p className="text-[14px] text-justify">
          Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse
          imperdiet justo sit amet feugiat aliquam. Curabitur tincidunt feugiat
          quam at porta. In pulvinar, arcu ac dapibus finibus, lorem mi dapibus
          lectus, eu porta ex magna id ligula. Integer imperdiet faucibus
          suscipit. Nullam a sem diam. Nam bibendum mollis massa, ut mollis odio
          luctus non. Nullam sit amet lacus maximus, ultrices velit nec, congue
          eros. Donec tincidunt tortor arcu, quis bibendum eros facilisis vitae.
          Sed dictum luctus metus, in blandit dui mattis quis. Ut in accumsan
          velit, quis lacinia justo. Aliquam ultrices luctus urna et
          condimentum.
        </p>
        <DialogFooter>
          <Button size="sm" type="button" onClick={() => setIsOpen(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SelfServiceOcrPage;
